import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { SearchApiServiceInterface } from "../interfaces/search-api-service.interface";
import { ViewerConfigurationService } from "../../../../../viewer-components/services/viewer-configuration.service";
import { SearchParametersQuery, SearchResult, SearchResultItem } from "../../../../model";
import { GeoService } from "../../../../../viewer-components/services/internal-services/geo.service";

/**
 * Maps one `/embedding/extract` GeoJSON feature to a `SearchResultItem` — the SINGLE mapping
 * shared by the API search path (`mapApiResponseToSearchResult`) and the chat's viewer handoff
 * (`ViewerHandoffService.toEmbeddings`). `properties.score` becomes `similarity`;
 * `collection`/`quadkey`/product type pass through; the geometry becomes WKT.
 */
export function embeddingFeatureToSearchItem(feature: any): SearchResultItem {
  const properties = feature?.properties ?? {};
  return {
    // Quadkey doubles as the stable item key (same convention as embeddings-api.service).
    key: properties.quadkey ?? "",
    geometry: GeoService.polygonToWkt(feature.geometry),
    similarity: properties.score ?? null,
    collection: properties.collection ?? null,
    quadkey: properties.quadkey ?? null,
    productType: properties["orientation-source"] ?? properties["source.product-type"] ?? null,
  } as SearchResultItem;
}

/**
 * Future implementation for the real search API
 * This service will make POST requests to the final search endpoint
 */
@Injectable({
  providedIn: "root",
})
export class ApiSearchService implements SearchApiServiceInterface {
  private readonly searchEndpoint = "/embedding/extract";

    private readonly cacheWarmUpEndpoint = "/embedding/cache";

  public get apiUrl(): string {
    return this.viewerConfigurationService.getViewerConfiguration().apiUrl + "/v2";
  }

  public get token(): string {
    return this.viewerConfigurationService.getViewerConfiguration().token;
  }

  public get httpHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: "Bearer " + this.token,
      Accept: "application/json",
      "Content-Type": "application/json",
    });
  }

  constructor(
    private viewerConfigurationService: ViewerConfigurationService,
    private http: HttpClient,
  ) {}

  /**
   * Get session from MapService initConfiguration
   * @private
   */
  private getSession(): string | null {
    return this.viewerConfigurationService.session ?? null;
  }

  /**
   * Wrapper for HTTP POST that automatically adds session parameter
   * @private
   */
  private post<T>(url: string, body: any, options?: any): Observable<any> {
    const session = this.getSession();
    const bodyWithSession = session ? { ...body, session } : body;
    return this.http.post<T>(url, bodyWithSession, options);
  }

  /**
   * Wrapper for HTTP GET that automatically adds session parameter
   * @private
   */
  private get<T>(url: string, options?: any): Observable<any> {
    const session = this.getSession();
    if (session) {
      const params = options?.params || {};
      const updatedOptions = { ...options, params: { ...params, session } };
      return this.http.get<T>(url, updatedOptions);
    }
    return this.http.get<T>(url, options);
  }

  /**
   * Performs a search query against the real API
   * @param params Search parameters using final API format
   * @returns Observable containing the search results
   */
  search(params: SearchParametersQuery): Observable<SearchResult> {
    const url = this.apiUrl + this.searchEndpoint;

    // Make POST request with SearchParametersQuery as body (session added automatically)
    // observe: "response" so the pagination headers (total-records) are readable alongside the body
    return this.post<any>(url, params, { headers: this.httpHeaders, observe: "response" }).pipe(
      map((response: HttpResponse<any>) =>
        this.mapApiResponseToSearchResult(response.body, params, this.readTotalRecords(response)),
      ),
      catchError((error) => {
        // Propagate the error with full details for proper error handling
        return throwError(() => error);
      }),
    );
  }

    /**
     * Checks whether a geometry filter is valid by warming up the embeddings cache for it.
     * This endpoint doesn't return a feature list — a 200 response means the geometry is valid.
     * @param params Search parameters (geometry filter only)
     * @returns Observable resolving to true on a successful (200) response
     */
    validateGeometry(params: SearchParametersQuery): Observable<boolean> {
        const url = this.apiUrl + this.cacheWarmUpEndpoint;

        return this.post<any>(url, params, {headers: this.httpHeaders}).pipe(
            map(() => true),
            catchError((error) => throwError(() => error)),
        );
    }

  /**
   * Reads the `total-records` pagination header. The API only sends it on page 1, and a
   * cross-origin browser can only read it if the server exposes it, so it may be missing.
   * @private
   * @returns The total, or undefined when the header is absent or not a valid number
   */
  private readTotalRecords(response: HttpResponse<unknown>): number | undefined {
    const raw = response.headers?.get("total-records");
    const total = raw === null || raw === undefined ? NaN : Number(raw);
    return Number.isFinite(total) ? total : undefined;
  }

  /**
   * Maps the API response to the standard SearchResult format
   * @private
   */
  private mapApiResponseToSearchResult(
    response: any,
    params: SearchParametersQuery,
    totalRecords?: number,
  ): SearchResult {
    const items = Array.isArray(response.features) ? response.features.map(embeddingFeatureToSearchItem) : [];
    return {
      total: response.total || 0,
      totalRecords,
      items: items || [],
      metadata: {
        page: params.page || 1,
        pageSize: params["page-size"] || 20,
        searchType: this.determineSearchTypeFromParams(params),
      },
    };
  }

  /**
   * Determines search type from parameters
   * @private
   */
  private determineSearchTypeFromParams(params: SearchParametersQuery): string {
    if (params["search-text"]) return "text";
    if (params["search-url"]) return "image-url";
    if (params["search-image"]) return "image-data";
    if (params["search-location"]) return "location";
    return "text";
  }
}
